import React from 'react';
import 'twin.macro';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import _last from 'lodash-es/last';
import { Infobox } from '@teetsh/app/src/components/Callout/Infobox';
import { TabsWithUnderline } from '@teetsh/app/src/components/Tabs/TabsWithUnderline';
import { EDTRestorableList } from '@teetsh/app/src/containers/UserSettings/Trash/EDT';
import { ProgrammationsRestorableList } from '@teetsh/app/src/containers/UserSettings/Trash/Programmations';
import { SequencesRestorableList } from '@teetsh/app/src/containers/UserSettings/Trash/Sequences';
import { FichesDePrepRestorableList } from '@teetsh/app/src/containers/UserSettings/Trash/FichesDePrep';
import { ElevesRestorableList } from '@teetsh/app/src/containers/UserSettings/Trash/Eleves';
import { SchoolyearsRestorableList } from '@teetsh/app/src/containers/UserSettings/Trash/Schoolyears';
import { SchoolsRestorableList } from '@teetsh/app/src/containers/UserSettings/Trash/Schools';
import { ClassroomsRestorableList } from '@teetsh/app/src/containers/UserSettings/Trash/Classrooms';
import { CarnetsDeNotesRestorableList } from '@teetsh/app/src/containers/UserSettings/Trash/CarnetsDeNotes';
import { NotesRestorableList } from '@teetsh/app/src/containers/UserSettings/Trash/Notes';

interface Item {
  label: string;
  component?: React.ReactNode;
}

export function Trash() {
  const { t } = useTranslation(['userSettings']);

  const items: Record<string, Item> = {
    schoolyear: {
      label: t('userSettings:restore.navbar.schoolyears'),
      component: <SchoolyearsRestorableList />,
    },
    school: {
      label: t('userSettings:restore.navbar.schools'),
      component: <SchoolsRestorableList />,
    },
    classrooms: {
      label: t('userSettings:restore.navbar.classrooms'),
      component: <ClassroomsRestorableList />,
    },
    carnetsDeNotes: {
      label: t('userSettings:restore.navbar.carnetsDeNotes'),
      component: <CarnetsDeNotesRestorableList />,
    },
    eleve: {
      label: t('userSettings:restore.navbar.eleves'),
      component: <ElevesRestorableList />,
    },
    schedule: {
      label: t('userSettings:restore.navbar.schedules'),
      component: <EDTRestorableList />,
    },
    programmation: {
      label: t('userSettings:restore.navbar.programmations'),
      component: <ProgrammationsRestorableList />,
    },
    sequence: {
      label: t('userSettings:restore.navbar.sequences'),
      component: <SequencesRestorableList />,
    },
    lesson: {
      label: t('userSettings:restore.navbar.lessons'),
      component: <FichesDePrepRestorableList />,
    },
    memo: {
      label: t('userSettings:restore.navbar.notes'),
      component: <NotesRestorableList />,
    },
  };

  const navigate = useNavigate();
  const handleNavbarTabChange = (tabId: string) => {
    navigate(`/settings/trash/${tabId}`);
  };

  const { pathname } = useLocation();
  const tabLocation = _last(pathname.split('/')) as string;
  const currentTabId = items[tabLocation] ? tabLocation : 'schoolyear';

  if (!items[currentTabId]) {
    return null;
  }

  return (
    <div tw="text-base">
      <Infobox intent="primary">
        {t('userSettings:restore.description')}
      </Infobox>
      <br />
      <div>
        <TabsWithUnderline
          onChange={handleNavbarTabChange}
          tabs={Object.entries(items).map(([id, { label }]) => ({
            id,
            label,
            current: id === currentTabId,
          }))}
          display="flex-wrap"
        />
        <div tw="mt-4">{items[currentTabId].component}</div>
      </div>
    </div>
  );
}
